import React, { useEffect, useMemo, useRef, useState } from 'react'
import datepickerIcon from 'styleguide/icons/ds2/calendarsingledatesolid.svg'
import lockIcon from '../../../../public/assets/images/lockSolid.svg'
import TooltipBanner from '../TooltipBanner'
import CalendarHeader from './CalendarHeader'
import {
  convertMMDDYYYYToYYYYMMDD,
  formatUTCTo12Hour,
} from '../../../utils/date'
import { getTimeOffTypeLabel } from '../../../utils/timeOffTypes'
import {
  formatMMDDYYYY,
  parseValue,
  isPastDate,
  isFutureDate,
  isBeforeDate,
  isAfterDate,
  buildCells,
  transformTableRowsToTooltipData,
  getCalendarViewButtonStyle,
  getCursorClass,
  getIconViewButtonStyle,
} from './datepickerUtils'
import type { Props, CalendarViewButtonProps, Cell } from './Datepicker.types'
import 'form-pattern/dist/main.css'
import './Datepicker.css'

const Datepicker: React.FC<Props> = ({
  name,
  label,
  value = '',
  required,
  helperText,
  disabled,
  error,
  onChange,
  placeholder,
  onBlur,
  onDateClick,
  onHover,
  onMonthYearChange,
  openBehaviour = 'calendarView',
  datesToBeHighlighted,
  otherSelectedDatesToShow,
  otherRequestDetails,
  tableRows,
  readOnly = false,
  isEditMode = false,
  editDate,
  editModeSelectedDates = [],
  canEnterEditMode = false,
  customInputStyle,
  calendarWidth,
  calendarHeight,
  highlightVacationBidOnHold = false,
  committedDates = [],
  disableFutureDates = false,
  disableBeforeDate = '',
  disableAfterDate = '',
  disableCommittedDateClick= false,
}) => {
const vacationBidStatuses = new Set([
  'SUBMITTED',
  'RESUBMITTED',
  'RESUBMITTED_ONHOLD',
  'RESUBMITTED_RELEASED',
  'PRTSUBMITTED',
  'APPROVED_PRTSUBMITTED',
  'PRTSUBMITTED_ONHOLD',
  'PRTSUBMITTED_RELEASED',
  'SUBMITTED_ONHOLD',
  'SUBMITTED_RELEASED',
])


  /**
   * Memoize effectiveOtherRequestDetails to maintain stable reference.
   * This prevents infinite render loops from useEffect dependencies on this value.
   */
  const effectiveOtherRequestDetails = useMemo(
    () =>
      otherRequestDetails ||
      (tableRows ? transformTableRowsToTooltipData(tableRows) : undefined),
    [otherRequestDetails, tableRows]
  )

  const isCalendarView = openBehaviour === 'calendarView'
  const today = new Date()
  const todayFormatted = formatMMDDYYYY(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  )

  const initial = parseValue(value, today)
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState(initial.month)
  const [year, setYear] = useState(initial.year)
  const initializeDates = (): string[] => {
    if (!isCalendarView || !value) return []
    return Array.isArray(value) ? value : [value]
  }
  const [selectedDates, setSelectedDates] =
    useState<string[]>(initializeDates())
  const [hoveredDate, setHoveredDate] = useState<string | null>(null)
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null)
  const [useFixedWidth, setUseFixedWidth] = useState(false)
  /** Fixed-position style computed for the iconView calendar so it escapes overflow-clipping containers. */
  const iconCalendarStyleRef = useRef<React.CSSProperties>({})
  const inputRef = useRef<HTMLInputElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const calendarRef = useRef<HTMLDialogElement | null>(null)
  const hoverClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Tracks whether the TooltipBanner detail modal is open so we do not
  // clear hoveredDate (and unmount the tooltip/modal) while it is visible.
  const tooltipDetailModalOpenRef = useRef(false)
  // Auto-closes the tooltip if the mouse rests on it without any movement
  // for a few seconds — hovering it must stay open long enough to reach
  // and click interactive content (e.g. "See more"), but shouldn't persist
  // forever just because the cursor happens to be sitting there idle.
  const idleCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const TOOLTIP_IDLE_CLOSE_MS = 3000
  // Tracks the number of selected dates from the previous render so the calendar
  // only auto-navigates to value[0]'s month on the very first selection (0 → 1+).
  // Subsequent date additions must not jump the calendar back to the first date's month.
  const initialValueLength = Array.isArray(value) ? value.length : 0
  const prevValueLengthRef = useRef<number>(initialValueLength)

  useEffect(() => {
    const measure = () => {
      // Trigger a layout recalculation to ensure wrapper width is accurate when calendar opens
      requestAnimationFrame(() => {
        // We'll use wrapperRef directly when calendar opens
      })
    }

    // Run on mount and on resize
    measure()
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('resize', measure)
    }
  }, [])

  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      const t = ev.target as Node | null
      if (!t || !open) return
      if (
        wrapperRef.current &&
        (wrapperRef.current === t || wrapperRef.current.contains(t))
      )
        return
      setOpen(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setOpen(false)
        if (inputRef.current) inputRef.current.focus()
      }
      if (ev.key === 'ArrowLeft') {
        ev.preventDefault()
        changeMonth(-1)
      }
      if (ev.key === 'ArrowRight') {
        ev.preventDefault()
        changeMonth(1)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  useEffect(() => {
    if (isCalendarView) {
      // For calendar view, ensure calendar is always open
      setOpen(true)
      // Keep internal selectedDates in sync with controlled value, including clear/reset.
      // Ignore string values which come from icon view on the same page.
      if (Array.isArray(value)) {
        const prevLength = prevValueLengthRef.current
        setSelectedDates(value)
        // Only auto-navigate to value[0]'s month when transitioning from 0 selected dates
        // to 1+ (e.g. initial load or first date selection). Never jump the calendar back
        // when the user adds additional dates while viewing a different month.
        if (value.length > 0 && prevLength === 0) {
          const parsed = parseValue(value[0], today)
          setMonth(parsed.month)
          setYear(parsed.year)
        }
        prevValueLengthRef.current = value.length
      }
    } else {
      const parsed = parseValue(value, today)
      setMonth(parsed.month)
      setYear(parsed.year)
    }
  }, [value, isCalendarView])

  // When the editDate changes, navigate the calendar to that date's month so the user
  // can see the date they are editing highlighted in context. Also notify the parent via
  // onMonthYearChange so it can refresh other-request data for that month.
  useEffect(() => {
    if (!isCalendarView || !editDate) return
    const parsed = parseValue(editDate, today)
    setMonth(parsed.month)
    setYear(parsed.year)
    if (onMonthYearChange) {
      onMonthYearChange(parsed.month, parsed.year)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDate, isCalendarView])
  useEffect(() => {
    return () => {
      if (hoverClearTimeoutRef.current) {
        clearTimeout(hoverClearTimeoutRef.current)
      }
      if (idleCloseTimeoutRef.current) {
        clearTimeout(idleCloseTimeoutRef.current)
      }
    }
  }, [])

  // Clear pending timeout when hoveredDate changes
  useEffect(() => {
    if (hoveredDate && hoverClearTimeoutRef.current) {
      // If we're now hovering a date, cancel any pending clear
      clearTimeout(hoverClearTimeoutRef.current)
      hoverClearTimeoutRef.current = null
    }
    if (!hoveredDate && idleCloseTimeoutRef.current) {
      clearTimeout(idleCloseTimeoutRef.current)
      idleCloseTimeoutRef.current = null
    }
  }, [hoveredDate])

  // Determine tooltip width based on hovered date and data
  useEffect(() => {
    if (hoveredDate && effectiveOtherRequestDetails) {
      const dateKey = convertMMDDYYYYToYYYYMMDD(hoveredDate)
      const tooltipData = effectiveOtherRequestDetails[dateKey]

      // Use fixed width when there's no detailed allocation data
      setUseFixedWidth(!tooltipData)
    } else if (hoveredDate && isToday(hoveredDate)) {
      // Fixed width for "Today's Date"
      setUseFixedWidth(true)
    } else {
      setUseFixedWidth(false)
    }
  }, [hoveredDate, effectiveOtherRequestDetails])

  // Helper: Handle edit mode date selection
  const handleEditModeSelection = (
    dayVal: string,
    datesList: string[],
    isAlreadySelected: boolean
  ): boolean => {
    if (!isEditMode || !editDate) return false

    if (isAlreadySelected && dayVal === editDate) {
      if (committedDates.includes(dayVal)) {
        // Committed date being edited - clicking shouldn't deselect it here;
        // removal must go through the explicit Remove action instead.
        if (onChange) onChange(name, datesList)
        return true
      }
      // Not yet committed (e.g. a fresh date that auto-entered edit mode
      // because it conflicts with another employee's partial-day request) -
      // allow toggling it off like any other in-progress selection.
      const newDates = datesList.filter((d) => d !== dayVal)
      setSelectedDates(newDates)
      if (onChange) onChange(name, newDates)
      return true
    }

    if (isAlreadySelected && dayVal !== editDate) {
      // Clicking a different already-selected date - switch edit target
      if (onChange) onChange(name, datesList)
      return true
    }

    if (!isAlreadySelected) {
      // Clicking a new date - add it
      const newDates = [...datesList, dayVal]
      setSelectedDates(newDates)
      if (onChange) onChange(name, newDates)
      return true
    }

    return false
  }

  // Helper: Handle canEnterEditMode prevention
  const handleCanEnterEditModePrevention = (
    isAlreadySelected: boolean
  ): boolean => {
    if (
      !isEditMode &&
      canEnterEditMode &&
      isAlreadySelected &&
      selectedDates.length > 1
    ) {
      if (onChange) onChange(name, selectedDates)
      return true
    }
    return false
  }

  // Helper: Handle normal mode toggle
  const handleNormalModeToggle = (
    dayVal: string,
    isAlreadySelected: boolean
  ) => {
    const newDates = isAlreadySelected
      ? selectedDates.filter((date) => date !== dayVal)
      : [...selectedDates, dayVal]

    setSelectedDates(newDates)
    if (onChange) onChange(name, newDates)
  }

  // Handler for date selection in calendar view (multi-select)
  const handleCalendarDateSelect = (dayVal: string) => {
    // In edit mode, use editModeSelectedDates from parent; otherwise use internal selectedDates
    const datesList =
      isEditMode && editModeSelectedDates
        ? editModeSelectedDates
        : selectedDates
    const isAlreadySelected = datesList.includes(dayVal)

    // Try edit mode handling first
    if (handleEditModeSelection(dayVal, datesList, isAlreadySelected)) {
      return
    }

    // Try canEnterEditMode prevention
    if (handleCanEnterEditModePrevention(isAlreadySelected)) {
      return
    }

    // Fall through to normal mode toggle
    handleNormalModeToggle(dayVal, isAlreadySelected)
  }

  // Helper function to check if a date is selected (works for both calendar and icon views)
  const isDateSelected = (dayVal: string): boolean => {
    if (isCalendarView) {
      return selectedDates.includes(dayVal)
    }
    return value === dayVal
  }

  // Helper function to check if a date is today
  const isToday = (dayVal: string): boolean => {
    return dayVal === todayFormatted
  }

  // Helper function to check if a date is in the past (before today)
  const changeMonth = (delta: number) => {
    let m = month + delta
    let y = year
    if (m < 0) {
      m = 11
      y -= 1
    }
    if (m > 11) {
      m = 0
      y += 1
    }
    setMonth(m)
    setYear(y)
    // Call parent callback to sync API with new month/year
    if (onMonthYearChange) {
      onMonthYearChange(m, y)
    }
  }

  const cells: Cell[] = buildCells(year, month)

  // When the iconView calendar opens, compute a fixed viewport-relative position so the
  // popup escapes any overflow-clipping ancestor (e.g. the table's overflow-x:auto wrapper).
  // Also syncs the calendar month/year to the current value so pre-filled dates are visible.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isCalendarView || !open || !wrapperRef.current) return

    // Navigate calendar to the pre-filled value's month/year every time it opens.
    // This ensures the correct month is shown even if the value arrived via props
    // after the initial mount (when today's date was used as the fallback).
    let strVal = ''

    if (typeof value === 'string') {
      strVal = value
    } else if (Array.isArray(value)) {
      strVal = value[0] ?? ''
    }

    if (strVal) {
      const parsed = parseValue(strVal, new Date())
      setMonth(parsed.month)
      setYear(parsed.year)
    }

    /** Computes fixed position from the wrapper's current viewport rect. */
    const computePosition = () => {
      if (!wrapperRef.current) return
      const rect = wrapperRef.current.getBoundingClientRect()
      const calendarW = Math.max(300, Math.round(rect.width))
      // Always open below the input field.
      const style: React.CSSProperties = {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${calendarW}px`,
      }
      iconCalendarStyleRef.current = style
    }

    // Set initial position after the DOM has painted.
    requestAnimationFrame(computePosition)

    // Re-compute on every scroll event (captured so all scroll containers are covered)
    // so the popup tracks the anchor element as the user scrolls.
    window.addEventListener('scroll', computePosition, {
      capture: true,
      passive: true,
    })
    return () =>
      window.removeEventListener('scroll', computePosition, { capture: true })
  }, [open, isCalendarView]) // value intentionally omitted: sync runs only on open transition

  // When calendar opens in calendarView mode, measure and set the wrapper width so calendar matches input
  // Measures calendar width when opened so it matches the wrapper/input width.
  useEffect(() => {
    if (!open || !calendarRef.current || !wrapperRef.current) return

    const measureCalendarWidth = () => {
      requestAnimationFrame(() => {
        if (!wrapperRef.current || !calendarRef.current) return

        const wrapperWidth = Math.round(wrapperRef.current.offsetWidth)

        if (wrapperWidth > 0) {
          calendarRef.current.style.width = `${wrapperWidth}px`
          calendarRef.current.style.setProperty(
            '--calendar-width',
            `${wrapperWidth}px`
          )
        }
      })
    }

    measureCalendarWidth()
  }, [open, isCalendarView])

  // Helper: Determine if date is selected based on mode
  const determineSelectionState = (dayVal: string) => {
    const isOtherRequest =
      Array.isArray(otherSelectedDatesToShow) &&
      otherSelectedDatesToShow.includes(dayVal)
    // Committed date: previously reviewed, shown in light blue and still clickable.
    // Only applies when not in edit mode and not an other-user's request date.
    const isCommittedSelected =
      !isEditMode && !isOtherRequest && committedDates.includes(dayVal)

    if (isEditMode && editDate && editModeSelectedDates) {
      const isSelected =
        dayVal === editDate ||
        (editModeSelectedDates.includes(dayVal) && !committedDates.includes(dayVal))
      const isCommittedSelected =
        !isSelected &&
        !isOtherRequest &&
        committedDates.includes(dayVal)
      const isOtherSelected =
        isOtherRequest ||
        (editModeSelectedDates.includes(dayVal) &&
          dayVal !== editDate &&
          !committedDates.includes(dayVal))
      return { isSelected, isOtherSelected, isCommittedSelected }
    }
    const isSelected = !isCommittedSelected && isDateSelected(dayVal)
    return { isSelected, isOtherSelected: isOtherRequest, isCommittedSelected }
  }

  /**
   * Returns a CSS class describing where this selected date sits within a
   * run of consecutive selected dates, so the UI can draw a continuous blue
   * band connecting them:
   *  - 'selected-single'      → isolated date (renders as a plain circle)
   *  - 'selected-range-start' → first date of a consecutive run
   *  - 'selected-range-middle'→ interior date of a consecutive run
   *  - 'selected-range-end'   → last date of a consecutive run
   * Row boundaries (Sunday ↔ Saturday) are treated as breaks so the band
   * never wraps across a calendar row.
   */
  const calculateSelectedDatePosition = (
    dayVal: string,
    cellIndex: number
  ): string => {
    const activeDates =
      isEditMode && editModeSelectedDates
        ? editModeSelectedDates
        : selectedDates
    if (!activeDates.includes(dayVal)) return ''

    const [m, d, y] = dayVal.split('/').map(Number)
    const prevDate = new Date(y, m - 1, d - 1)
    const nextDate = new Date(y, m - 1, d + 1)
    const prevDayVal = formatMMDDYYYY(
      prevDate.getFullYear(),
      prevDate.getMonth(),
      prevDate.getDate()
    )
    const nextDayVal = formatMMDDYYYY(
      nextDate.getFullYear(),
      nextDate.getMonth(),
      nextDate.getDate()
    )

    // cellIndex % 7 === 0 → Sunday (start of row), % 7 === 6 → Saturday (end of row)
    const hasContinuousPrev =
      activeDates.includes(prevDayVal) && cellIndex % 7 !== 0
    const hasContinuousNext =
      activeDates.includes(nextDayVal) && cellIndex % 7 !== 6

    if (!hasContinuousPrev && !hasContinuousNext) return 'selected-single'
    if (!hasContinuousPrev && hasContinuousNext) return 'selected-range-start'
    if (hasContinuousPrev && !hasContinuousNext) return 'selected-range-end'
    return 'selected-range-middle'
  }

  // Helper: Calculate highlight position based on neighboring cells
  const calculateHighlightPosition = (
    isHighlighted: boolean,
    cellIndex: number
  ): string => {
    if (!isHighlighted) return ''
    const highlightedSet = new Set(datesToBeHighlighted || [])
    const prevCell = cellIndex > 0 ? cells[cellIndex - 1] : null
    const nextCell = cellIndex < cells.length - 1 ? cells[cellIndex + 1] : null
    const prevHighlighted =
      prevCell?.inMonth &&
      highlightedSet.has(formatMMDDYYYY(year, month, prevCell.day))
    const nextHighlighted =
      nextCell?.inMonth &&
      highlightedSet.has(formatMMDDYYYY(year, month, nextCell.day))

    if (!prevHighlighted && !nextHighlighted) return 'single'
    if (!prevHighlighted && nextHighlighted) return 'start'
    if (prevHighlighted && !nextHighlighted) return 'end'
    return 'middle'
  }

  // Render day button with special styling for today in calendar view
  const renderDayButton = (
    c: { day: number; inMonth: boolean },
    cellIndex: number
  ) => {
    const uniqueKey = c.inMonth
      ? `${year}-${month}-${c.day}`
      : `disabled-${year}-${month}-${c.day}`

    if (!c.inMonth) {
      return (
        <div
          key={uniqueKey}
          className="day h-full flex items-center justify-center"
        >
          <div className="disabled-day" aria-hidden="true">
            {c.day}
          </div>
        </div>
      )
    }

    const dayVal = formatMMDDYYYY(year, month, c.day)
    const isTodayDate = isToday(dayVal)
    // A date is disabled if it falls in the blocked future range, before the
    // minimum allowed start date, or (by default) in the past.
    const baseDisabled = disableFutureDates
      ? isFutureDate(dayVal, today)
      : isPastDate(dayVal, today)
    const isDisabled = !isTodayDate && (
      baseDisabled ||
      isBeforeDate(dayVal, disableBeforeDate) ||
      isAfterDate(dayVal, disableAfterDate)
    )
    const isHighlighted =
      Array.isArray(datesToBeHighlighted) &&
      datesToBeHighlighted.includes(dayVal)

    const { isSelected, isOtherSelected, isCommittedSelected } =
      determineSelectionState(dayVal)
    const isOtherRequest =
      Array.isArray(otherSelectedDatesToShow) &&
      otherSelectedDatesToShow.includes(dayVal)
    const isPartialDayRequest =
      isOtherRequest &&
      effectiveOtherRequestDetails?.[convertMMDDYYYYToYYYYMMDD(dayVal)]
        ?.durationLabel === 'Partial Day'
    // Determine if this is an on-hold date so it can be styled orange and remain clickable
    const otherRequestDetail = effectiveOtherRequestDetails?.[
      convertMMDDYYYYToYYYYMMDD(dayVal)
    ] as any

const isVacationBidRequest =
  otherRequestDetail?.requestType === 'Vacation Bid' ||
  otherRequestDetail?.allocations?.some(
    (alloc: any) => alloc.typeLabel === 'Vacation Bid'
  )

// Check if there is any Draft/Returned request on this date
const hasDraftOrReturned =
  Array.isArray(otherRequestDetail?.otherRequests) &&
  otherRequestDetail.otherRequests.some(
    (req: any) => req.status === 'DRAFT' || req.status === 'RETURNED'
  )

const isOnHold =
  highlightVacationBidOnHold &&
  isOtherRequest &&
  !!otherRequestDetail &&
  isVacationBidRequest &&
  vacationBidStatuses.has(otherRequestDetail.status) &&
  !hasDraftOrReturned
    const highlightPosition = calculateHighlightPosition(
      isHighlighted,
      cellIndex
    )
    const highlightClass = highlightPosition ? `range-${highlightPosition}` : ''
    const selectedRangeClass = isCalendarView
      ? calculateSelectedDatePosition(dayVal, cellIndex)
      : ''

    if (isCalendarView) {
      return renderCalendarViewButton({
        key: uniqueKey,
        highlightClass,
        selectedRangeClass,
        dayVal,
        isDisabled,
        isOtherRequest,
        isPartialDayRequest,
        isOnHold,
        isVacationBidRequest,
        isSelected,
        isOtherSelected,
        isCommittedSelected,
        isHighlighted,
        isTodayDate,
        dayNumber: c.day,
      })
    }

    return renderIconViewButton(
      uniqueKey,
      highlightClass,
      dayVal,
      isSelected,
      c.day,
      isDisabled,
      isTodayDate
    )
  }

  // Render button for calendar view
  const renderCalendarViewButton = ({
    key,
    highlightClass,
    selectedRangeClass,
    dayVal,
    isDisabled,
    isOtherRequest,
    isPartialDayRequest,
    isOnHold,
    isVacationBidRequest,
    isSelected,
    isOtherSelected,
    isCommittedSelected,
    isHighlighted,
    isTodayDate,
    dayNumber,
  }: CalendarViewButtonProps) => {
    const { buttonClasses, buttonStyles, hasBottomDot } =
      getCalendarViewButtonStyle({
        isDisabled,
        isOtherRequest,
        isPartialDayRequest,
        isOnHold,
        isVacationBidRequest,
        isSelected,
        isOtherSelected,
        isCommittedSelected,
        isHighlighted,
        isTodayDate,
      })
    const cursorClass = getCursorClass(isDisabled, readOnly, buttonClasses)

    return (
      <div
        key={key}
        className={`day h-full flex items-center justify-center ${highlightClass} ${selectedRangeClass}`}
      >
        <button
          type="button"
          aria-pressed={isSelected}
          aria-disabled={isDisabled}
          style={buttonStyles}
          className={`w-9 h-9 rounded-full flex flex-col items-center justify-center relative ${buttonClasses} ${cursorClass}`}
          onClick={() => {
            // Editing a committed/reviewed entry (opened via the review
            // table's pencil icon) must block all other calendar clicks so
            // the user can't wander off to a different date mid-edit. A
            // fresh, not-yet-committed date that auto-entered edit mode
            // (e.g. a partial-day date conflicting with another employee's
            // request) has no such restriction — it must stay clickable so
            // it can be toggled off or another date picked instead.
            if (isEditMode && !!editDate && committedDates.includes(editDate)) {
              return;
            }
            const isCommittedInParent = committedDates.includes(dayVal)
            const isAddingNewDate = !isEditMode

            if (disableCommittedDateClick && isCommittedInParent && isAddingNewDate) {
              return
            }

            const isSelectedInEditSession =
              isEditMode &&
              Array.isArray(editModeSelectedDates) &&
              editModeSelectedDates.includes(dayVal)
            const isCommittedSelectedDate =
              isCommittedInParent && isSelectedInEditSession
            // Highlighted dates belong to the current draft/request.
            // The getOtherReqDetails API returns ALL requests for the month,
            // which can include the current draft's own dates — making
            // isOtherRequest true for a date that IS the user's draft.
            // Guard only on isHighlighted so those dates always open the form
            // regardless of whether another request also falls on the same day.
            const isOwnedDraftDate = isHighlighted
         
            const det = effectiveOtherRequestDetails?.[
              convertMMDDYYYYToYYYYMMDD(dayVal)
              ] as any

            const partialRequestCount =
              det?.durationLabel === 'Partial Day'
                ? (det?.individualRequests?.length ?? 1)
                : 0

            const isMaxPartialDayRequest = partialRequestCount >= 3

            const allocationsTotal = Array.isArray(det?.allocations)
              ? det.allocations.reduce(
                  (sum: number, allocation: any) =>
                    sum + (Number(allocation.hours) || 0),
                  0
                )
              : Number(det?.totalHours) || 0

            const totalHoursOnDate =
              det?.individualRequests?.length > 0
                ? det.individualRequests.reduce(
                    (total: number, request: any) => {
                      const requestHours = (request.allocations || []).reduce(
                        (sum: number, allocation: any) =>
                          sum + (Number(allocation.hours) || 0),
                        0
                      )

                      return total + requestHours
                    },
                    0
                  )
                : allocationsTotal

            const isMaxHoursReached = totalHoursOnDate >= 16

            // Hard stop:
            // If date already has 3 partial-day requests or 16+ total hours,
            // do nothing. No form open, no validation banner, no selection.
            if (isMaxPartialDayRequest || isMaxHoursReached) {
              return
            }

            // Block other-request dates unless they are owned draft dates,
            // on-hold, or partial-day (which leaves part of the day open).
            let blocked =
              isOtherSelected && !isOwnedDraftDate && !isCommittedSelectedDate
            // Unblock on-hold dates whenever the parent supplies a click handler —
            // the handler (CreateVacationBidRequest, DraftView) explicitly routes
            // SUBMITTED_ONHOLD dates to their own entry flow.  Using onDateClick
            // instead of canEnterEditMode allows clicks even before the history
            // table has been populated (canEnterEditMode = showHistoryTable starts
            // as false on the Create page).
            if (blocked && onDateClick && effectiveOtherRequestDetails) {
              const isVacationBidRequest =
                det?.allocations?.some(
                  (alloc: any) =>
                    alloc?.typeLabel === 'Vacation Bid' ||
                    alloc?.typeLabel === 'Vacation Bidding'
                ) ?? false

              const  isOnHoldStatus =vacationBidStatuses.has(det?.status)
              const hasDraftOrReturned =Array.isArray(det?.otherRequests) &&det.otherRequests.some((req: any) => req.status === 'DRAFT' || req.status === 'RETURNED')
              const isPastCalendarDate = isPastDate(dayVal, today)
              if (
                highlightVacationBidOnHold &&
                isOnHoldStatus &&
                isVacationBidRequest &&
                !hasDraftOrReturned &&
                !isPastCalendarDate
              ) {
                blocked = false
              }
            }
            if (blocked && isPartialDayRequest) blocked = false

            // Only fire the parent callback for valid (non-disabled, non-blocked) clicks.
            // Disabled dates (e.g. past dates) must not trigger onDateClick — callers
            // such as EditSubmittedRequest use this callback to add dates to the request,
            // so omitting this guard was silently allowing past-date selection on edit
            // and resubmit (regression fix).
            if (!isDisabled && !blocked) {
              onDateClick?.(dayVal)
            }
            // Skip the internal toggle for owned draft dates — the parent
            // manages their selection state via `value`/`datesToBeHighlighted`.
            // Toggling them causes a deselection flash that the parent useEffect
            // then undoes, producing a visible flicker.
            if (
              !isDisabled &&
              !blocked &&
              !readOnly &&
              !isCommittedSelected &&
              !(isEditMode && isCommittedInParent) &&
              !isOwnedDraftDate
            ) {
              handleCalendarDateSelect(dayVal)
            }
          }}
          onMouseEnter={(e) => handleDateHoverEnter(dayVal, e)}
          onMouseLeave={() => handleDateHoverLeave()}
        >
          {dayNumber}
          {hasBottomDot && (
            <span className="absolute bottom-0 w-1.5 h-1.5 bg-[#0078B3] rounded-full"></span>
          )}
        </button>
      </div>
    )
  }

  // Render button for icon view
  const renderIconViewButton = (
    key: string,
    highlightClass: string,
    dayVal: string,
    isSelected: boolean,
    dayNumber: number,
    isDisabled: boolean,
    isTodayDate: boolean
  ) => {
    const { buttonClasses, buttonStyles } = getIconViewButtonStyle(
      isSelected,
      isDisabled,
      isTodayDate
    )

    return (
      <div
        key={key}
        className={`day ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${highlightClass}`}
      >
        <button
          type="button"
          aria-pressed={isSelected}
          aria-disabled={isDisabled}
          style={buttonStyles}
          className={`w-9 h-9 rounded-full flex flex-col items-center justify-center relative ${buttonClasses} ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={() => {
            if (!isDisabled && onChange) {
              onChange(name, dayVal)
              setOpen(false)
            }
          }}
        >
          {dayNumber}
          {isTodayDate && (
            <span className="absolute bottom-0 w-1.5 h-1.5 bg-[#0078B3] rounded-full"></span>
          )}
        </button>
      </div>
    )
  }

  const calculateTooltipTotalHours = (tooltipData: {
    totalHours?: number
    individualRequests?: Array<{
      allocations: Array<{
        typeLabel: string
        hours: number
      }>
    }>
  }) => {
    if (
      tooltipData.individualRequests &&
      tooltipData.individualRequests.length > 0
    ) {
      return tooltipData.individualRequests.reduce((total, request) => {
        return (
          total +
          request.allocations.reduce(
            (sum, allocation) => sum + allocation.hours,
            0
          )
        )
      }, 0)
    }

    return tooltipData.totalHours ?? 0
  }

  // Whether a date has its own tooltip-worthy content (other requests, or
  // it's today with no other requests). Used to stop the mouse from
  // hijacking an already-open tooltip merely by transiting over a plain
  // neighboring cell on its way toward the tooltip (the tooltip is
  // positioned diagonally above-left of the hovered cell, so reaching it
  // often crosses adjacent cells first).
  const hasTooltipContent = (dayVal: string): boolean => {
    if (effectiveOtherRequestDetails) {
      const dateKey = convertMMDDYYYYToYYYYMMDD(dayVal)
      if (effectiveOtherRequestDetails[dateKey]) return true
    }
    return isToday(dayVal)
  }

  // Restarts the idle-close timer: if the mouse rests on the tooltip without
  // any further movement for TOOLTIP_IDLE_CLOSE_MS, it closes on its own —
  // unless the detail modal is genuinely open, which is a real, ongoing
  // interaction rather than idling.
  const resetIdleCloseTimer = () => {
    if (idleCloseTimeoutRef.current) {
      clearTimeout(idleCloseTimeoutRef.current)
    }
    idleCloseTimeoutRef.current = setTimeout(() => {
      if (tooltipDetailModalOpenRef.current) return
      setHoveredDate(null)
      setHoveredRect(null)
      if (onHover) {
        onHover(null, null)
      }
      idleCloseTimeoutRef.current = null
    }, TOOLTIP_IDLE_CLOSE_MS)
  }

  // Helper: Handle mouse enter on date
  const handleDateHoverEnter = (
    dayVal: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    // Entering a plain cell while a content-bearing tooltip is already
    // showing for a different date: don't hijack it immediately — treat this
    // like leaving, so the mouse still has its usual grace period to reach
    // the tooltip that's floating above the original cell.
    if (
      !hasTooltipContent(dayVal) &&
      hoveredDate &&
      hoveredDate !== dayVal &&
      hasTooltipContent(hoveredDate)
    ) {
      handleDateHoverLeave()
      return
    }

    if (hoverClearTimeoutRef.current) {
      clearTimeout(hoverClearTimeoutRef.current)
      hoverClearTimeoutRef.current = null
    }
    const rect = e.currentTarget.getBoundingClientRect()
    setHoveredDate(dayVal)
    setHoveredRect(rect)
    if (hasTooltipContent(dayVal)) {
      resetIdleCloseTimer()
    }
    if (onHover) {
      onHover(dayVal, rect)
    }
  }

  // Helper: Handle mouse leave on date
  const handleDateHoverLeave = () => {
    hoverClearTimeoutRef.current = setTimeout(() => {
      setHoveredDate(null)
      setHoveredRect(null)
      if (onHover) {
        onHover(null, null)
      }
      hoverClearTimeoutRef.current = null
    }, 300)
  }

  // OLD RENDER DAY BUTTON CODE DELETED - REPLACED WITH ABOVE REFACTORED VERSION

  // Render calendar popup for icon view
  const renderIconView = () => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let input = e.target.value

      // If completely empty, pass empty string
      if (!input || input.trim() === '') {
        if (onChange) onChange(name, '')
        return
      }

      // Allow only digits and slashes; strip everything else
      input = input.replace(/[^\d/]/g, '')

      if (onChange) onChange(name, input)
    }

    // On blur: pad single-digit month/day with a leading zero so the value
    // always conforms to MM/DD/YYYY before validation runs.
    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const raw = e.target.value.trim()
      if (raw) {
        const parts = raw.split('/')
        const mm = parts[0] ? parts[0].padStart(2, '0') : ''
        const dd = parts[1] ? parts[1].padStart(2, '0') : ''
        const yyyy = parts[2] ?? ''
        const padded = [mm, dd, yyyy].filter(Boolean).join('/')
        if (padded !== raw && onChange) onChange(name, padded)
      }
      if (onBlur) onBlur(e)
    }

    return (
      <div
        className="w-full min-w-[300px] date-field-wrapper relative"
        ref={wrapperRef}
      >
        <div className="input-with-icon">
          <input
            id={`date-input-${name}`}
            name={name}
            ref={inputRef}
            type="text"
            className={`w-full min-w-[300px] block date-input-field !text-black ${disabled ? 'ds-form__disabled-field bg-gray-100 text-gray-600' : ''} ${error ? 'field-error' : ''}`}
            style={customInputStyle}
            value={value}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={(e) => {
              // open calendar on Enter, Space or ArrowDown from input
              const k = e.key
              if (
                k === 'Enter' ||
                k === 'ArrowDown' ||
                k === ' ' ||
                k === 'Spacebar' ||
                k === 'Space'
              ) {
                e.preventDefault()
                setOpen(true)
              }
            }}
            placeholder={placeholder ?? todayFormatted}
            maxLength={10}
            required={required}
            disabled={disabled}
          />
          <button
            type="button"
            aria-label={
              disabled
                ? `Date field is locked for ${label ?? name}`
                : `Open calendar for ${label ?? name}`
            }
            onClick={() => !disabled && setOpen((s) => !s)}
            aria-expanded={open}
            disabled={disabled}
            className="date-input-icon"
          >
            <img
              src={disabled ? lockIcon : datepickerIcon}
              alt={disabled ? 'locked' : 'calendar'}
            />
          </button>
        </div>

        {open && !disabled && (
          <dialog
            id={`custom-calendar-${name}`}
            ref={calendarRef}
            className="custom-calendar bg-white border rounded shadow-lg p-3 absolute top-12 left-0"
            aria-label={`Calendar for ${label ?? name}`}
            open={open}
          >
            <CalendarHeader
              month={month}
              year={year}
              onPrev={() => changeMonth(-1)}
              onNext={() => changeMonth(1)}
              openBehaviour={openBehaviour}
            />
            <div className="grid grid-cols-7 gap-1 text-center text-sm">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                <div key={d} className="font-medium">
                  {d}
                </div>
              ))}
              {cells.map((c, idx) => renderDayButton(c, idx))}
            </div>
          </dialog>
        )}
      </div>
    )
  }

  // Resolve tooltip header and message for the currently hovered date
  const resolveTooltipContent = (): {
    tooltipHeader: React.ReactNode
    tooltipMessage: React.ReactNode
    isPartialDay: boolean
    hasMultipleRequests: boolean
    individualRequests?: any[]
  } => {
    if (!hoveredDate)
      return {
        tooltipHeader: null,
        tooltipMessage: null,
        isPartialDay: false,
        hasMultipleRequests: false,
        individualRequests: undefined,
      }

    if (hoveredDate && effectiveOtherRequestDetails) {
      const dateKey = convertMMDDYYYYToYYYYMMDD(hoveredDate)
      const tooltipData = effectiveOtherRequestDetails[dateKey]

      if (tooltipData) {
        const hasMultipleRequests =
          (tooltipData.individualRequests?.length ?? 0) > 1
        const isBirthdayTooltip = tooltipData.allocations.some(
          (alloc) => getTimeOffTypeLabel(alloc.typeLabel) === 'Birthday'
        )
        const totalHours = calculateTooltipTotalHours(tooltipData)

        // With multiple separate requests on the same partial day, the full
        // per-allocation + start/end breakdown (one request's start/end time
        // plus every allocation across every request) can run to 6+ lines,
        // which overflows the hover preview's line-clamp and renders as an
        // unpredictable partial list instead of a clean summary. The full,
        // correctly-attributed breakdown is already available via "See more"
        // (individualRequests, rendered per-request in the detail modal), so
        // the hover preview only needs a concise pointer to it here.
        const lines = hasMultipleRequests
          ? [
              ` ${tooltipData.date}`,
              `${tooltipData.individualRequests!.length} requests on this date`,
              `Total hours on this date: ${totalHours.toFixed(2)}`,
            ]
          : [
              ` ${tooltipData.date}`,
              ...(tooltipData.durationLabel === 'Partial Day' &&
              tooltipData.startTime &&
              tooltipData.endTime
                ? [
                    `Start: ${formatUTCTo12Hour(tooltipData.startTime)}`,
                    `End: ${formatUTCTo12Hour(tooltipData.endTime)}`,
                  ]
                : []),
              ...tooltipData.allocations.map((alloc) => {
                const mappedLabel = getTimeOffTypeLabel(alloc.typeLabel)
                if (mappedLabel === 'Birthday') return 'Full Day Birthday'
                const suffix = mappedLabel === 'Vacation' ? ' (PTO/ETO)' : ''
                return `${mappedLabel}${suffix} (${alloc.hours.toFixed(2)} hours)`
              }),
              ...(isBirthdayTooltip
                ? []
                : [`Total hours on this date: ${totalHours.toFixed(2)}`]),
            ]
        return {
          tooltipHeader: tooltipData.durationLabel,
          tooltipMessage: (
            <div className="space-y-1 flex flex-col gap-1">
              {lines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          ),
          isPartialDay: tooltipData.durationLabel === 'Partial Day',
          hasMultipleRequests,
          individualRequests: tooltipData.individualRequests,
        }
      }
      if (isToday(hoveredDate)) {
        return {
          tooltipHeader: null,
          tooltipMessage: <div>Today's Date</div>,
          isPartialDay: false,
          hasMultipleRequests: false,
          individualRequests: undefined,
        }
      }
    } else if (
      isToday(hoveredDate) &&
      !effectiveOtherRequestDetails?.[convertMMDDYYYYToYYYYMMDD(hoveredDate)]
    ) {
      return {
        tooltipHeader: null,
        tooltipMessage: <div>Today's Date</div>,
        isPartialDay: false,
        hasMultipleRequests: false,
        individualRequests: undefined,
      }
    }

    return {
      tooltipHeader: null,
      tooltipMessage: null,
      isPartialDay: false,
      hasMultipleRequests: false,
      individualRequests: undefined,
    }
  }

  // Render full calendar view (always visible, multi-select)
  const renderCalendarView = () => {
    const {
      tooltipHeader,
      tooltipMessage,
      isPartialDay,
      hasMultipleRequests,
      individualRequests,
    } = resolveTooltipContent()

    // Only the partial-day, multiple-requests tooltip has clickable content
    // ("See more"). Plain informational tooltips have nothing to reach, so
    // they follow the strict rule: hover state belongs to the date cell,
    // and merely hovering the tooltip must not keep it open.
    const hasSeeMoreOption = isPartialDay && hasMultipleRequests

    // Open the request form pre-filled empty (0 hours) for a partial-day date so the
    // remaining time on that date can be requested. Mirrors a normal date click.
    const handlePartialDaySeeMore = () => {
      if (!hoveredDate) return
      // Guard: do not process disabled (e.g. past) dates from the tooltip "See more" button.
      const baseDisabled = disableFutureDates
        ? isFutureDate(hoveredDate, today)
        : isPastDate(hoveredDate, today)
      const isDisabledHovered =
        baseDisabled ||
        isBeforeDate(hoveredDate, disableBeforeDate) ||
        isAfterDate(hoveredDate, disableAfterDate)
      if (isDisabledHovered) return
      onDateClick?.(hoveredDate)
      if (!readOnly) handleCalendarDateSelect(hoveredDate)
      setHoveredDate(null)
      setHoveredRect(null)
      onHover?.(null, null)
    }

    const calendarContainerStyle: React.CSSProperties = {
      ...(calendarWidth ? { width: `${calendarWidth}px` } : {}),
      ...(calendarHeight
        ? {
            height: `${calendarHeight}px`,
            display: 'flex',
            flexDirection: 'column',
          }
        : {}),
    }
    const calendarGridStyle: React.CSSProperties = calendarHeight
      ? { flex: 1, alignContent: 'stretch' }
      : {}

    return (
      <div className="date-field-wrapper mt-4 w-fit relative" ref={wrapperRef}>
        <div
          className="bg-white border rounded shadow-lg p-3 min-w-[300px] max-w-md"
          style={calendarContainerStyle}
        >
          <CalendarHeader
            month={month}
            year={year}
            onPrev={() => changeMonth(-1)}
            onNext={() => changeMonth(1)}
            openBehaviour={openBehaviour}
          />
          <div
            className="grid grid-cols-7 text-center text-sm"
            style={calendarGridStyle}
          >
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
              <div
                key={d}
                className="font-medium flex items-center justify-center"
              >
                {d}
              </div>
            ))}
            {cells.map((c, idx) => renderDayButton(c, idx))}
          </div>
        </div>

        {/* Tooltip for hovered date */}
        {hoveredDate && hoveredRect && tooltipMessage && (
          <div
            className="datepicker-tooltip-container"
            role="tooltip"
            style={{
              position: 'fixed',
              left: `max(16px, calc(${hoveredRect.left}px - 50px))`,
              top: `calc(${hoveredRect.top}px - 12px)`,
              transform: 'translateY(-100%)',
              zIndex: 9999,
              width: useFixedWidth ? '150px' : 'auto',
              maxWidth: 'calc(100vw - 32px)',
            }}
            onMouseEnter={() => {
              if (hasSeeMoreOption) {
                // This tooltip has a clickable "See more" — bridge the hover
                // so the mouse has time to reach and click it, with an idle
                // timer as a safety net against it lingering unattended.
                if (hoverClearTimeoutRef.current) {
                  clearTimeout(hoverClearTimeoutRef.current)
                  hoverClearTimeoutRef.current = null
                }
                resetIdleCloseTimer()
                return
              }
              // No interactive content to reach — hover state belongs to the
              // date cell only. Only protect a genuinely open detail modal.
              if (
                tooltipDetailModalOpenRef.current &&
                hoverClearTimeoutRef.current
              ) {
                clearTimeout(hoverClearTimeoutRef.current)
                hoverClearTimeoutRef.current = null
              }
            }}
            onMouseMove={hasSeeMoreOption ? resetIdleCloseTimer : undefined}
            onMouseLeave={() => {
              // Only schedule the hover-clear if the tooltip detail modal is NOT open.
              // When the modal is open we must keep this container mounted so the
              // portal modal (which lives in TooltipBanner) is not destroyed.
              if (tooltipDetailModalOpenRef.current) return
              hoverClearTimeoutRef.current = setTimeout(() => {
                setHoveredDate(null)
                setHoveredRect(null)
                if (onHover) {
                  onHover(null, null)
                }
                hoverClearTimeoutRef.current = null
              }, 300)
            }}
          >
            <TooltipBanner
              header={tooltipHeader}
              message={tooltipMessage}
              width="w-[280px] sm:w-[310px]"
              padding="p-2! sm:p-3!"
              individualRequests={
                isPartialDay && hasMultipleRequests
                  ? individualRequests
                  : undefined
              }
              onSeeMore={
                isPartialDay && hasMultipleRequests
                  ? handlePartialDaySeeMore
                  : undefined
              }
              onClose={() => {
                setHoveredDate(null)
                setHoveredRect(null)
                if (onHover) {
                  onHover(null, null)
                }
              }}
              onModalStateChange={(isOpen) => {
                tooltipDetailModalOpenRef.current = isOpen
                // When modal closes, clear the hovered date so tooltip hides cleanly.
                if (!isOpen) {
                  setHoveredDate(null)
                  setHoveredRect(null)
                  if (onHover) onHover(null, null)
                }
              }}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`ds-form ${isCalendarView ? '' : 'w-full'}`}
      data-ds-theme="vessel"
      data-ds-variant="input"
      data-ds-version="1"
    >
      {!isCalendarView && label && (
        <label htmlFor={`date-input-${name}`} style={{ color: '#003B71' }}>
          {label}
        </label>
      )}

      {!isCalendarView && renderIconView()}

      {isCalendarView && renderCalendarView()}

      {!isCalendarView &&
        (error ? (
          <div className="error-description">
            <ul id="text-input-error-description-list-1">
              <li>
                <img
                  src="/assets/images/errorCircleSmall.svg"
                  alt="Error icon"
                  width="14"
                  height="14"
                />
                <span className="screenreader-only">Error</span>
                {error}
              </li>
            </ul>
          </div>
        ) : (
          <span className="ds-form__text-input-helper">{helperText}</span>
        ))}
    </div>
  )
}

export default Datepicker
